import { captureMessage } from '@sentry/nextjs';
import { User } from 'oidc-client-ts';
import { AuthContextProps } from 'react-oidc-context';

import { BACKEND_ENDPOINTS } from '@/constant/network';
import { META_DATA_VALUES, META_TAGS, OIDC_AUTH_STATUES } from '@/constant/oidc';
import { oidcApiV1 } from '@/services/http';
import { OIDCClientLoginState } from '@/types/oidc';
import { PageData } from '@/types/ssr';

import { equals, isNotEmpty } from './common';

export const isExpiredSession = (expiresAt?: number, expiresBuffer = 0) => {
  if (!expiresAt) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return expiresAt - expiresBuffer < currentTime;
};

export const isSessionValid = async (expiresAt?: number, expiresBuffer = 0) => {
  try {
    const isExpired = isExpiredSession(expiresAt, expiresBuffer);
    if (isExpired) return false;

    const loginState = await oidcApiV1.get<OIDCClientLoginState>({ url: BACKEND_ENDPOINTS.oidcLoginState });

    return equals(loginState.data.state, OIDC_AUTH_STATUES.authorized);
  } catch (err) {
    captureMessage(`failed check is valid session, error: ${err}`, { level: 'warning' });
    return false;
  }
};

export const handleSilentRenew = async (
  signinSilent: AuthContextProps['signinSilent'],
  signoutRedirect?: AuthContextProps['signoutRedirect'],
) => {
  try {
    await signinSilent();
  } catch (error) {
    captureMessage(`Silent renew failed: ${error}`, { level: 'warning' });
    signoutRedirect?.();
  }
};

export const getShortUserInitials = (user?: User | null) => {
  if (!user?.profile) return '';
  const given_name = user.profile.given_name || '';
  const family_name = user.profile.family_name || '';

  return `${given_name.at(0) ?? ''} ${family_name.at(0) ?? ''}`;
};

export const checkIsPrivatePage = (pageData?: PageData) => {
  const metaAccess = pageData?.meta?.access;

  if (isNotEmpty(metaAccess)) {
    return equals(metaAccess, META_DATA_VALUES.private);
  }
  return Boolean(pageData?.metatags?.includes(META_TAGS.private));
};
